import {
    IAccount,
    IReclassesAccounts,
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { keyOf, refsEqual, resolveRef } from './reclassificationAccountKey';

type AccountLike = Pick<IAccount, 'accountNumber' | 'name'> | Pick<IReclassesAccounts, 'accountNumber' | 'name'>;

/**
 * Pure helpers operating on the transactional reclassifications list.
 * No mutation; safe to call from selectors and templates.
 */

/**
 * Net reclass sum for an account in a given period (source = -amount, target = +amount).
 * Uses composite-key equality on refs.
 */
export function getReclassSumForRef(
    transactions: IReclassificationTransaction[] | undefined,
    accountRef: IReclassificationAccountRef,
    period: ReclassificationPeriod,
): number {
    if (!Array.isArray(transactions) || transactions.length === 0) return 0;
    let sum = 0;
    for (const t of transactions) {
        if (t.period !== period) continue;
        if (refsEqual(t.source, accountRef)) sum -= t.amount;
        if (refsEqual(t.target, accountRef)) sum += t.amount;
    }
    return sum;
}

/** Transactions that have this account on the source side, in the given period. */
export function getOutgoingTransactions(
    transactions: IReclassificationTransaction[] | undefined,
    sourceRef: IReclassificationAccountRef,
    period: ReclassificationPeriod,
): IReclassificationTransaction[] {
    if (!Array.isArray(transactions)) return [];
    return transactions.filter((t) => t.period === period && refsEqual(t.source, sourceRef));
}

/** Transactions that have this account on the target side, in the given period. */
export function getIncomingTransactions(
    transactions: IReclassificationTransaction[] | undefined,
    targetRef: IReclassificationAccountRef,
    period: ReclassificationPeriod,
): IReclassificationTransaction[] {
    if (!Array.isArray(transactions)) return [];
    return transactions.filter((t) => t.period === period && refsEqual(t.target, targetRef));
}

/**
 * Identify transactions whose source or target cannot be resolved in the
 * current statement-specific account lists. A transaction is a conflict if
 * either leg fails composite-key resolution.
 */
export function findConflicts(
    transactions: IReclassificationTransaction[] | undefined,
    accountsByStatement: Record<ReclassificationStatement, AccountLike[]>,
): IReclassificationTransaction[] {
    if (!Array.isArray(transactions) || transactions.length === 0) return [];
    return transactions.filter((t) => {
        const accs = accountsByStatement[t.statement];
        if (!Array.isArray(accs)) return true;
        const src = resolveRef(t.source, accs);
        const tgt = resolveRef(t.target, accs);
        return src == null || tgt == null;
    });
}

/**
 * Group conflict transactions by the unresolved account ref. Each group
 * represents a single conflict card in the UI.
 */
export interface ConflictGroup {
    accountRef: IReclassificationAccountRef;
    statement: ReclassificationStatement;
    transactions: IReclassificationTransaction[];
}

export function groupConflictsByAccount(
    conflicts: IReclassificationTransaction[],
    accountsByStatement: Record<ReclassificationStatement, AccountLike[]>,
): ConflictGroup[] {
    const groups = new Map<string, ConflictGroup>();
    for (const t of conflicts) {
        const accs = accountsByStatement[t.statement] ?? [];
        const candidates: Array<{ ref: IReclassificationAccountRef; statement: ReclassificationStatement }> = [];
        if (resolveRef(t.source, accs) == null) candidates.push({ ref: t.source, statement: t.statement });
        if (resolveRef(t.target, accs) == null) candidates.push({ ref: t.target, statement: t.statement });

        for (const c of candidates) {
            const key = `${c.statement}::${keyOf(c.ref)}`;
            let g = groups.get(key);
            if (g == null) {
                g = { accountRef: c.ref, statement: c.statement, transactions: [] };
                groups.set(key, g);
            }
            g.transactions.push(t);
        }
    }
    return Array.from(groups.values());
}

/**
 * Apply a transactions list onto a flat list of accounts. Mutates the
 * accounts in place: sets `openingReClass` from PY transactions and
 * `closingReClass` from CY transactions. Unresolved refs are silently
 * skipped (their net effect surfaces as a conflict via `findConflicts`).
 *
 * NOTE: This function does NOT zero existing reclass values first — the
 * caller is expected to reset all account `openingReClass`/`closingReClass`
 * to 0 before invoking. This matches the engine's lifecycle.
 */
export function applyTransactionsToAccounts(
    transactions: IReclassificationTransaction[] | undefined,
    accountsByStatement: Record<ReclassificationStatement, AccountLike[]>,
): void {
    if (!Array.isArray(transactions) || transactions.length === 0) return;
    for (const t of transactions) {
        const accs = accountsByStatement[t.statement];
        if (!Array.isArray(accs)) continue;
        const field = t.period === ReclassificationPeriod.CURRENT_YEAR ? 'closingReClass' : 'openingReClass';
        const src = resolveRef(t.source, accs) as (IAccount & Record<string, unknown>) | undefined;
        const tgt = resolveRef(t.target, accs) as (IAccount & Record<string, unknown>) | undefined;
        if (src) (src[field] as number | undefined) = ((src[field] as number | undefined) ?? 0) - t.amount;
        if (tgt) (tgt[field] as number | undefined) = ((tgt[field] as number | undefined) ?? 0) + t.amount;
    }
}
